import React, { useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["draft", "planning", "upcoming", "live", "completed", "archived"];
const TYPES = ["Incentive Trip", "Leadership Event", "Sales Kickoff", "Summit", "Conference", "Other"];

export default function CreateEventModal({ open, onClose, onCreated }) {
  const { setAdminEvent, refreshEvents } = useAdminEvent();
  const [f, setF] = useState({ name: "", event_type: "Incentive Trip", description: "", start_date: "", end_date: "", city: "", country: "", host_resort: "", destination_airport: "", event_owner: "", status: "planning", estimated_attendance: "", approved_budget: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Event name is required"); return; }
    setBusy(true);
    try {
      const ev = await api.createEvent(f);
      await refreshEvents();
      setAdminEvent(ev.id);
      toast.success(`Created ${ev.name}`, { description: "Opening its workspace…" });
      onCreated && onCreated(ev);
      onClose();
    } catch (e) { toast.error("Could not create event", { description: e?.response?.data?.detail }); }
    setBusy(false);
  };

  const modal = (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-[hsl(189_62%_6%/0.5)] backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto no-scrollbar premium-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-[hsl(var(--primary))] flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-[hsl(var(--secondary))]" /> Create New Event</h2>
              <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"><X className="h-5 w-5" /></button>
            </div>

            <Group title="Basic Information">
              <Field label="Event Name *"><input data-testid="create-event-name" value={f.name} onChange={set("name")} className="inp" placeholder="e.g. Leadership Summit 2027" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Event Type"><select value={f.event_type} onChange={set("event_type")} className="inp">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Status"><select value={f.status} onChange={set("status")} className="inp capitalize">{STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
              </div>
              <Field label="Description"><textarea value={f.description} onChange={set("description")} rows={2} className="inp" /></Field>
            </Group>

            <Group title="Date & Location">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start Date"><input type="date" value={f.start_date} onChange={set("start_date")} className="inp" /></Field>
                <Field label="End Date"><input type="date" value={f.end_date} onChange={set("end_date")} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City"><input value={f.city} onChange={set("city")} className="inp" /></Field>
                <Field label="Country"><input value={f.country} onChange={set("country")} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primary Venue / Hotel"><input value={f.host_resort} onChange={set("host_resort")} className="inp" /></Field>
                <Field label="Airport Code"><input value={f.destination_airport} onChange={set("destination_airport")} className="inp" /></Field>
              </div>
            </Group>

            <Group title="Management (optional)">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Event Owner"><input value={f.event_owner} onChange={set("event_owner")} className="inp" /></Field>
                <Field label="Estimated Attendance"><input value={f.estimated_attendance} onChange={set("estimated_attendance")} className="inp" /></Field>
              </div>
            </Group>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="rounded-full border border-[hsl(var(--border))] px-5 py-2.5 text-sm font-semibold text-[hsl(var(--primary))]">Cancel</button>
              <button data-testid="create-event-submit" onClick={submit} disabled={busy} className="rounded-full bg-[hsl(var(--primary))] text-white px-5 py-2.5 text-sm font-semibold">{busy ? "Creating…" : "Create Event"}</button>
            </div>
            <style>{`.inp{width:100%;border:1px solid hsl(var(--border));border-radius:0.6rem;padding:0.5rem 0.75rem;font-size:0.875rem;background:white;color:hsl(var(--primary))}`}</style>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
  return typeof document !== "undefined" ? createPortal(modal, document.body) : null;
}

function Group({ title, children }) {
  return <div className="mb-4"><div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] mb-2">{title}</div><div className="space-y-3">{children}</div></div>;
}
function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-[hsl(var(--primary))] mb-1 block">{label}</span>{children}</label>;
}
